import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import NotFound from './pages/Lost/404.jsx';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from 'store';
import { ProtectedRoute } from 'api/middleware/authMiddleware';
import { authService } from 'api/services/auth';
import Loading from 'pages/Loading/Loading';
import { Navbar, ToastProvider } from 'components';
import { SearchLauncher } from 'components';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useGlobalNotificationListener } from 'hooks/useGlobalNotificationListener';
import useRouteHandler from 'hooks/useRouteHandler.js';
import './App.scss';

const Login = lazy(() => import('./pages/Login/Login'));
const ResetPassword = lazy(() =>
  import('./pages/Login/components/ResetPassword')
);
const Admin = lazy(() => import('./pages/Admin/Admin'));
const Build = lazy(() => import('./pages/Build/Build'));
const Prompts = lazy(() => import('./pages/Prompts/Prompts'));
const Contracts = lazy(() => import('./pages/Contracts/Contracts'));
const Landing = lazy(() => import('./pages/Landing/Landing.jsx'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const EmailVerification = lazy(() =>
  import('./pages/Login/components/EmailVerification.jsx')
);
// Settings is now a tab within Profile page

function AppRoutes() {
  const dispatch = useDispatch();

  useGlobalNotificationListener();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const { shouldSkipAuthForRoute } = useRouteHandler();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if current route should skip authentication (function gets path automatically)
        const routeCheck = shouldSkipAuthForRoute();

        // Skip auth check for routes that don't require authentication
        if (routeCheck.shouldSkipAuth) {
          return;
        }

        await authService.checkAuth(dispatch);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Auth check failed:', error);
        }
      }
    };

    checkAuthentication();
  }, [dispatch]);

  return (
    <>
      {isAuthenticated && <SearchLauncher />}
      <Suspense
        fallback={<div className='flex-justify-center '>Loading...</div>}
      >
        <Routes>
          <Route path='/' element={<Landing />} />
          <Route path='/login' element={<Login />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='/loading' element={<Loading />} />
          <Route
            path='/build/*'
            element={
              <ProtectedRoute>
                <Build />
              </ProtectedRoute>
            }
          />
          <Route
            path='/prompts/*'
            element={
              <ProtectedRoute>
                <Prompts />
              </ProtectedRoute>
            }
          />
          <Route
            path='/contracts/*'
            element={
              <ProtectedRoute>
                <Contracts />
              </ProtectedRoute>
            }
          />
          <Route
            path='/profile'
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path='/profile/:userId'
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin'
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route path='/confirm-email' element={<EmailVerification />} />
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

// Main App component
function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Provider store={store}>
        <ToastProvider>
          <BrowserRouter>
            <div className='App'>
              <div className='app-navbar-wrapper'>
                <Navbar title='GIGACORE' />
              </div>
              <div className='app-content'>
                <AppRoutes />
              </div>
            </div>
          </BrowserRouter>
        </ToastProvider>
      </Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
